import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Upload, X, Image as ImageIcon, CheckCircle, AlertCircle, Loader2, FileImage, FolderOpen } from 'lucide-react';

interface ImageFile {
  id: string;
  file: File;
  preview: string | null; // null when previews are skipped for large batches
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface ImageUploaderProps {
  onUpload: (files: File[], batchName?: string) => Promise<void>;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  isUploading?: boolean;
  uploadProgress?: number;
}

// Above this threshold, skip individual preview thumbnails to avoid browser memory issues
const PREVIEW_THRESHOLD = 50;

export function ImageUploader({
  onUpload,
  maxFiles = 500,
  maxFileSize = 15 * 1024 * 1024, // 15MB
  isUploading = false,
  uploadProgress = 0
}: ImageUploaderProps) {
  const [selectedFiles, setSelectedFiles] = useState<ImageFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [batchName, setBatchName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff'];

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: `Invalid file type: ${file.type}` };
    }
    if (file.size > maxFileSize) {
      return { valid: false, error: `File too large (max ${Math.round(maxFileSize / 1024 / 1024)}MB)` };
    }
    return { valid: true };
  };

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const remaining = maxFiles - selectedFiles.length;

    if (remaining <= 0) {
      alert(`Maximum ${maxFiles} files already selected.`);
      return;
    }

    // Accept up to the remaining capacity instead of rejecting all
    const trimmed = fileArray.length > remaining;
    const filesToAdd = trimmed ? fileArray.slice(0, remaining) : fileArray;

    // Skip preview thumbnails when the total would exceed the threshold
    const totalAfterAdd = selectedFiles.length + filesToAdd.length;
    const skipPreviews = totalAfterAdd > PREVIEW_THRESHOLD;

    const newFiles: ImageFile[] = filesToAdd.map((file) => {
      const validation = validateFile(file);
      return {
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        preview: skipPreviews ? null : URL.createObjectURL(file),
        status: (validation.valid ? 'pending' : 'error') as ImageFile['status'],
        error: validation.error,
      };
    }).filter(f => f.status === 'pending' || f.status === 'error');

    if (trimmed) {
      alert(`Only the first ${filesToAdd.length} of ${fileArray.length} files were added (maximum ${maxFiles}).`);
    }

    setSelectedFiles(prev => [...prev, ...newFiles]);
  }, [selectedFiles.length, maxFiles, maxFileSize]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    // Reset input to allow selecting same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [addFiles]);

  const removeFile = useCallback((id: string) => {
    setSelectedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    selectedFiles.forEach(file => {
      if (file.preview) URL.revokeObjectURL(file.preview);
    });
    setSelectedFiles([]);
  }, [selectedFiles]);

  const handleUpload = useCallback(async () => {
    const validFiles = selectedFiles.filter(f => f.status === 'pending').map(f => f.file);
    const nameToUse = batchName.trim() || undefined;

    if (validFiles.length > 0) {
      try {
        await onUpload(validFiles, nameToUse);
        setBatchName('');
        // Revoke object URLs inside the state updater so React unmounts
        // the <img> elements in the same render cycle, avoiding broken-image flashes
        setSelectedFiles(prev => {
          prev.forEach(file => {
            if (file.preview) URL.revokeObjectURL(file.preview);
          });
          return [];
        });
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  }, [selectedFiles, batchName, onUpload]);

  const validFileCount = selectedFiles.filter(f => f.status === 'pending').length;
  const errorFileCount = selectedFiles.filter(f => f.status === 'error').length;

  // Whether we're in "summary mode" (no individual thumbnails)
  const showPreviews = selectedFiles.length <= PREVIEW_THRESHOLD;

  // Compute total size for the summary view
  const totalSizeMB = useMemo(() => {
    const bytes = selectedFiles.reduce((sum, f) => sum + f.file.size, 0);
    return (bytes / (1024 * 1024)).toFixed(1);
  }, [selectedFiles]);

  return (
    <div className="space-y-6">
      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-12 cursor-pointer
          transition-all duration-300 text-center
          ${isDragOver
            ? 'border-primary bg-primary/10 scale-[1.02]'
            : 'border-border hover:border-primary/50 hover:bg-muted/50'
          }
          ${isUploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={allowedTypes.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-4">
          <div className={`p-4 rounded-full transition-colors ${isDragOver ? 'bg-primary/20' : 'bg-muted'}`}>
            <Upload className={`w-10 h-10 ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-semibold">
              {isDragOver ? 'Drop your images here' : 'Drag & drop images here'}
            </h3>
            <p className="text-sm text-muted-foreground">
              or click to browse files
            </p>
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>Supported: JPEG, PNG, WebP, GIF, BMP, TIFF</p>
            <p>Up to {maxFiles} files, {Math.round(maxFileSize / 1024 / 1024)}MB each</p>
          </div>
        </div>
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <Card>
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <div className="flex-1">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Uploading images...</span>
                  <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Files */}
      {selectedFiles.length > 0 && !isUploading && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-lg">Selected Images</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {validFileCount} valid{errorFileCount > 0 && <>, <span className="text-destructive">{errorFileCount} invalid</span></>}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={clearAll}>
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showPreviews ? (
              /* Thumbnail grid for smaller batches */
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 max-h-96 overflow-y-auto p-1">
                {selectedFiles.map((file) => (
                  <div
                    key={file.id}
                    className={`relative group aspect-square rounded-lg overflow-hidden border-2 ${
                      file.status === 'error' ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    {file.preview ? (
                      <img
                        src={file.preview}
                        alt={file.file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}

                    {/* Status Overlay */}
                    {file.status === 'error' && (
                      <div className="absolute inset-0 bg-destructive/80 flex items-center justify-center">
                        <div className="text-center text-white p-2">
                          <AlertCircle className="w-6 h-6 mx-auto mb-1" />
                          <p className="text-xs">{file.error}</p>
                        </div>
                      </div>
                    )}

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(file.id);
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Filename */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-xs text-white truncate">{file.file.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Compact summary for large batches (> PREVIEW_THRESHOLD files) */
              <div className="space-y-3">
                <div className="flex items-center gap-6 py-4 px-2">
                  <div className="p-4 rounded-full bg-primary/10">
                    <ImageIcon className="w-10 h-10 text-primary" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-lg font-semibold">
                      {validFileCount} {validFileCount === 1 ? 'image' : 'images'} ready to upload
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Total size: {totalSizeMB} MB
                    </p>
                  </div>
                </div>

                {/* List invalid files so the user knows what failed and why */}
                {errorFileCount > 0 && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
                    <p className="text-sm font-medium text-destructive flex items-center gap-1.5">
                      <AlertCircle className="w-4 h-4" />
                      {errorFileCount} {errorFileCount === 1 ? 'file' : 'files'} cannot be uploaded
                    </p>
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {selectedFiles
                        .filter(f => f.status === 'error')
                        .map(f => (
                          <li key={f.id} className="text-xs text-muted-foreground flex items-center justify-between gap-2">
                            <span className="truncate">{f.file.name}</span>
                            <span className="text-destructive shrink-0">{f.error}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Batch Name Input */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex items-end gap-4">
                <div className="flex-1 max-w-sm space-y-2">
                  <Label htmlFor="batch-name" className="text-sm font-medium flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Batch Name (optional)
                  </Label>
                  <Input
                    id="batch-name"
                    placeholder="e.g., Product Photos Summer 2025"
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                    className="h-10"
                  />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={clearAll}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={validFileCount === 0}
                    className="gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Upload {validFileCount} {validFileCount === 1 ? 'Image' : 'Images'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {selectedFiles.length === 0 && !isUploading && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <FileImage className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <CardDescription>
              No images selected. Drag and drop or click the area above to add images.
            </CardDescription>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
